
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, FileText, TrendingUp, Sparkles, Brain } from 'lucide-react';
import { LucideIcon } from 'lucide-react';

interface PersonalityTestResult {
  id: string;
  type: string;
  typeName: string;
  icon: LucideIcon;
  date: string;
  scores: Record<string, number>;
  summary: string;
  analysisContent?: any;
  createdAt: Date;
  updatedAt: Date;
}

interface PersonalityTestCardProps {
  result: PersonalityTestResult;
  isFirst: boolean;
  onViewResult: () => void;
  onViewAnalysis?: (analysis: any) => void;
}

export default function PersonalityTestCard({ 
  result, 
  isFirst, 
  onViewResult,
  onViewAnalysis 
}: PersonalityTestCardProps) {
  const IconComponent = result.icon;

  const handleViewAnalysis = () => {
    if (result.analysisContent && onViewAnalysis) {
      onViewAnalysis(result.analysisContent);
    }
  };

  const hasAnalysis = result.analysisContent && Object.keys(result.analysisContent).length > 0;
  const isUltraDetailed = hasAnalysis && result.analysisContent.detailed_trait_analysis;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: isFirst ? 0 : 0.1 }}
    >
      <Card className="transition-all duration-300 hover:shadow-lg relative">
        {hasAnalysis && (
          <div className="absolute top-2 right-2">
            <Badge className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
              <Sparkles className="h-3 w-3 mr-1" />
              {isUltraDetailed ? 'IA Ultra-Detalhada' : 'Análise IA'}
            </Badge>
          </div>
        )}
        
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-purple-600 text-white">
                <IconComponent className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  {result.typeName}
                  {isFirst && (
                    <Badge variant="secondary" className="text-xs">
                      Mais Recente
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1">
                  <Calendar className="h-4 w-4" />
                  {result.date}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              <span className="text-sm text-green-700">Concluído</span>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">Resumo dos Resultados</h4>
            <p className="text-sm text-muted-foreground">{result.summary}</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button 
              onClick={onViewResult}
              className="flex-1"
              variant="outline"
            >
              <FileText className="mr-2 h-4 w-4" />
              Ver Resultados
            </Button>
            
            {hasAnalysis && (
              <Button 
                onClick={handleViewAnalysis}
                className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                <Brain className="mr-2 h-4 w-4" />
                {isUltraDetailed ? 'Relatório Ultra-Detalhado (o3)' : 'Ver Relatório IA'}
              </Button>
            )}
          </div>

          {hasAnalysis && isUltraDetailed && (
            <div className="pt-2 border-t">
              <div className="flex items-center gap-2 text-xs text-purple-600">
                <Brain className="h-3 w-3" />
                <span>Análise gerada pelo modelo OpenAI o3 - Custo: 50 Seshs</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
